import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { getSchemaId } from 'dashboard/lib/workflows/platform-object';
import { deepTransformKeys } from 'dashboard/lib/workflows/util';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import {
  appendWorkflowCriteriaVariableSchemas,
  clearWorkflowCriteriaVariableSchemas,
  setWorkflowActions,
  setWorkflowFilterFieldConfigs,
  setWorkflowOperators,
  setWorkflowPrimitives,
} from 'dashboard/redux/actions/workflow-schema';
import { workflowSchemaEndpoints } from 'dashboard/redux/api/workflow-schema';
import { getPlatformObjectJsonSchema } from 'dashboard/redux/selectors/workflow-editor-context';
import { addObjectSchemas, ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint } from 'lib/interfaces/api';

// Schema groupings to be fetched in parallel with whatever isn't in these groups
export const SCHEMA_OBJECTS_EXTRACTED_GROUP_1 = [WorkflowFieldObjectKey.Inquiry];

export const SCHEMA_OBJECTS_EXTRACTED_GROUP_2 = [
  WorkflowFieldObjectKey.ReportAddressLookup,
  WorkflowFieldObjectKey.ReportAdverseMedia,
  WorkflowFieldObjectKey.ReportBusinessAdverseMedia,
  WorkflowFieldObjectKey.ReportBusinessIndustryClassification,
  WorkflowFieldObjectKey.ReportBusinessLiensLookup,
  WorkflowFieldObjectKey.ReportBusinessLookup,
  WorkflowFieldObjectKey.ReportBusinessRegistrationsLookup,
  WorkflowFieldObjectKey.ReportBusinessSocialMedia,
  WorkflowFieldObjectKey.ReportBusinessWatchlist,
  WorkflowFieldObjectKey.ReportBusinessWebsite,
  WorkflowFieldObjectKey.ReportChainalysisAddressScreening,
  WorkflowFieldObjectKey.ReportClearbitBusinessLookup,
  WorkflowFieldObjectKey.ReportCoinbaseCheckCryptoRisk,
  WorkflowFieldObjectKey.ReportCryptoAddressWatchlist,
  WorkflowFieldObjectKey.ReportCustomList,
  WorkflowFieldObjectKey.ReportEmailAddress,
  WorkflowFieldObjectKey.ReportEquifaxOneview,
  WorkflowFieldObjectKey.ReportKyckrBusinessLookup,
  WorkflowFieldObjectKey.ReportMiddeskBusinessLookup,
  WorkflowFieldObjectKey.ReportMxAccount,
  WorkflowFieldObjectKey.ReportPhoneNumber,
  WorkflowFieldObjectKey.ReportPoliticallyExposedPerson,
  WorkflowFieldObjectKey.ReportProfile,
  WorkflowFieldObjectKey.ReportScreening,
  WorkflowFieldObjectKey.ReportSentilinkScores,
  WorkflowFieldObjectKey.ReportSocialMedia,
  WorkflowFieldObjectKey.ReportSynthetic,
  WorkflowFieldObjectKey.ReportTrmWalletScreening,
  WorkflowFieldObjectKey.ReportWatchlist,
];

export const SCHEMA_OBJECTS_EXTRACTED_GROUP_3 = [
  WorkflowFieldObjectKey.VerificationAamva,
  WorkflowFieldObjectKey.VerificationCertificateKorea,
  WorkflowFieldObjectKey.VerificationDatabase,
  WorkflowFieldObjectKey.VerificationDatabaseAadhaar,
  WorkflowFieldObjectKey.VerificationDatabaseBusiness,
  WorkflowFieldObjectKey.VerificationDatabaseEcbsv,
  WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier,
  WorkflowFieldObjectKey.VerificationDatabaseStandard,
  WorkflowFieldObjectKey.VerificationDatabaseSerpro,
  WorkflowFieldObjectKey.VerificationDatabaseTin,
  WorkflowFieldObjectKey.VerificationDatabaseVatNumber,
  WorkflowFieldObjectKey.VerificationDigitalIdUkSharecode,
  WorkflowFieldObjectKey.VerificationDocument,
  WorkflowFieldObjectKey.VerificationEmailAddress,
  WorkflowFieldObjectKey.VerificationGovernmentId,
  WorkflowFieldObjectKey.VerificationGovernmentIdNfc,
  WorkflowFieldObjectKey.VerificationMobileDriversLicense,
  WorkflowFieldObjectKey.VerificationPhoneNumber,
  WorkflowFieldObjectKey.VerificationSelfie,
];

export const loadWorkflowSchemaParallel = (
  {
    loadCriteriaVariableSchemas = true,
  }: {
    loadCriteriaVariableSchemas: boolean;
  } = { loadCriteriaVariableSchemas: true },
): DashboardThunkAction<void> => {
  return async (dispatch) => {
    dispatch(clearWorkflowCriteriaVariableSchemas());

    await Promise.all([
      ...(loadCriteriaVariableSchemas
        ? [
            (async () => {
              const { meta } = await SessionClientDashboard.getResourceMetaTyped(
                ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas,
                {
                  filter: {
                    objects: [
                      ...SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
                      ...SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
                      ...SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
                    ],
                    mode: 'exclude',
                  },
                },
              );

              if (meta == null) {
                return;
              }

              dispatch(appendWorkflowCriteriaVariableSchemas(meta['criteria-variable-schemas']));
            })(),
            (async () => {
              const { meta } = await SessionClientDashboard.getResourceMetaTyped(
                ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas,
                {
                  filter: {
                    objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
                    mode: 'include',
                  },
                },
              );

              if (meta == null) {
                return;
              }

              dispatch(appendWorkflowCriteriaVariableSchemas(meta['criteria-variable-schemas']));
            })(),
            (async () => {
              const { meta } = await SessionClientDashboard.getResourceMetaTyped(
                ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas,
                {
                  filter: {
                    objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
                    mode: 'include',
                  },
                },
              );

              if (meta == null) {
                return;
              }

              dispatch(appendWorkflowCriteriaVariableSchemas(meta['criteria-variable-schemas']));
            })(),
            (async () => {
              const { meta } = await SessionClientDashboard.getResourceMetaTyped(
                ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas,
                {
                  filter: {
                    objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
                    mode: 'include',
                  },
                },
              );

              if (meta == null) {
                return;
              }

              dispatch(appendWorkflowCriteriaVariableSchemas(meta['criteria-variable-schemas']));
            })(),
          ]
        : []),
      (async () => {
        const { meta } = await SessionClientDashboard.getResourceMetaTyped(
          ApiEndpoint.WorkflowSchemaOperators,
          {
            filter: {},
          },
        );

        if (meta == null) {
          return;
        }

        dispatch(setWorkflowOperators(meta.operators));
      })(),
      (async () => {
        const { meta } = await SessionClientDashboard.getResourceMetaTyped(
          ApiEndpoint.WorkflowSchemaWorkflowActions,
          {
            filter: {},
          },
        );

        if (meta == null) {
          return;
        }

        dispatch(setWorkflowActions(meta['generic-workflow-actions']));
      })(),
      (async () => {
        const { meta } = await SessionClientDashboard.getResourceMetaTyped(
          ApiEndpoint.WorkflowSchemaPrimitives,
          {
            filter: {},
          },
        );

        if (meta == null) {
          return;
        }

        dispatch(setWorkflowPrimitives(deepTransformKeys(meta.primitives)));
      })(),
    ]);
  };
};

export const loadWorkflowSchemaForObject = (
  objectKey: WorkflowFieldObjectKey,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const { meta } = await SessionClientDashboard.getResourceMetaTyped(
      ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas,
      {
        filter: {
          objects: [objectKey],
          mode: 'include',
        },
      },
    );

    if (meta == null) {
      return;
    }

    dispatch(appendWorkflowCriteriaVariableSchemas(meta['criteria-variable-schemas']));
  };
};

export const loadFilterFieldConfigs = (): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const { meta } = await SessionClientDashboard.getResourceMetaTyped(
      ApiEndpoint.WorkflowSchemaFilterFieldConfigs,
      {
        filter: {},
      },
    );

    if (meta == null) {
      return;
    }
    dispatch(setWorkflowFilterFieldConfigs(meta['filter-field-configs']));
  };
};

export const fetchBaseObjectJsonSchemas = (
  templates: TemplateFilter[],
): DashboardThunkAction<Promise<ObjectSchemaMap>> => {
  return async (dispatch) => {
    const schemas = await dispatch(
      workflowSchemaEndpoints.getBaseWorkflowObjectJsonSchemas.initiate(templates),
    ).unwrap();

    return schemas;
  };
};

export const fetchObjectJsonSchema = (
  template?: TemplateFilter,
): DashboardThunkAction<Promise<ObjectSchemaMap>> => {
  return async (dispatch, getState) => {
    const platformObjectJsonSchema = getPlatformObjectJsonSchema(getState());
    if (
      !template ||
      !platformObjectJsonSchema ||
      Object.keys(platformObjectJsonSchema).includes(getSchemaId(template.type, template.id))
    ) {
      return {};
    }

    const schema = await dispatch(
      workflowSchemaEndpoints.getWorkflowObjectJsonSchema.initiate({
        mode: 'exactly',
        objects: [template.type],
        'template-id': template.id,
      }),
    ).unwrap();
    dispatch(addObjectSchemas(schema));

    return schema;
  };
};
